// What a Service work's Status colour means. The sheet keeps Smartsheet's own colour balls (Red, Yellow,
// Green, Gray) and the app keeps their colours; this gives each one its name and what it says about the
// work, for the status dropdown, its hover card, the filter chips, the Gantt legend and the printout:
//   🔴 Red     N/A – Not Applicable   the work or inspection doesn't apply to this vessel
//   🟡 Yellow  In Progress            being worked on, not finished yet
//   🟢 Green   Completed              the work is done
//   ⚪ Gray    N/T – Not Tested       the inspection or functional test hasn't been done yet
// A work is closed when it is Completed or N/A, as the sheet's own Completion column counts it
// (Green + Red over all works); In Progress and N/T are still to do.

export type ServiceStatusKey = 'na' | 'progress' | 'done' | 'nt';

export interface ServiceStatusMeaning {
  key: ServiceStatusKey;
  /** the colour as people say it */
  colour: 'Red' | 'Yellow' | 'Green' | 'Grey';
  /** the short name a chip or a legend shows ("N/A") */
  short: string;
  /** the full name ("N/A – Not Applicable") */
  label: string;
  /** what it means for the work */
  description: string;
}

/** the four statuses, in the sheet's order. */
export const SERVICE_STATUSES: readonly ServiceStatusMeaning[] = [
  { key: 'na', colour: 'Red', short: 'N/A', label: 'N/A – Not Applicable', description: 'This work item or inspection does not apply to the vessel.' },
  { key: 'progress', colour: 'Yellow', short: 'In Progress', label: 'In Progress', description: 'Work is currently in progress and has not yet been completed.' },
  { key: 'done', colour: 'Green', short: 'Completed', label: 'Completed', description: 'The assigned work has been completed.' },
  { key: 'nt', colour: 'Grey', short: 'N/T', label: 'N/T – Not Tested', description: 'The required inspection or functional test has not yet been performed.' },
];

const byKey = (k: ServiceStatusKey) => SERVICE_STATUSES.find((s) => s.key === k)!;
// the Status cell's value as Smartsheet stores it (its colour's name), any case
const BY_VALUE: Record<string, ServiceStatusMeaning> = {
  red: byKey('na'),
  yellow: byKey('progress'),
  green: byKey('done'),
  gray: byKey('nt'),
  grey: byKey('nt'),
};

/** what a Status value means, or null for a blank or a value that isn't one of the colours. */
export function serviceStatusOf(value: string | null | undefined): ServiceStatusMeaning | null {
  return BY_VALUE[String(value ?? '').trim().toLowerCase()] ?? null;
}

/** a Status value's full name ("N/A – Not Applicable"); any other value as it is. */
export const serviceStatusLabel = (value: string | null | undefined): string => serviceStatusOf(value)?.label ?? String(value ?? '').trim();

/** a Status value's short name ("N/A"); any other value as it is. */
export const serviceStatusShort = (value: string | null | undefined): string => serviceStatusOf(value)?.short ?? String(value ?? '').trim();

/** a Status value in words, name and meaning ("N/A – Not Applicable: This work item … the vessel."), for
 *  a tooltip or a screen reader; any other value as it is. */
export function serviceStatusText(value: string | null | undefined): string {
  const s = serviceStatusOf(value);
  return s ? `${s.label}: ${s.description}` : String(value ?? '').trim();
}

/** whether a Status closes the work: Completed, or N/A (it doesn't apply), as the sheet's Completion counts. */
export const serviceStatusCloses = (value: string | null | undefined): boolean => {
  const k = serviceStatusOf(value)?.key;
  return k === 'done' || k === 'na';
};
